// frontend/src/features/onboarding/steps/EchoStyleStep.tsx
// Simplified: single style selector (style implies format), Echo badge inline on step header only.
// Keeps identical onNext() data shape.
import React, { useState } from 'react'
import { ArrowRight, ArrowLeft } from 'lucide-react'

interface EchoStyleStepProps {
  eyebrow: string
  accentClass: string
  btnClass: string
  initialData?: {
    briefing_style: string
    template_style: string
    format_style: string
    prose_paragraph_length: string
    enable_story_integration: boolean
  }
  onNext: (data: any) => void
  onBack: () => void
}

const STYLES = [
  {
    value: 'conversational',
    label: 'Conversational',
    desc: 'Like a well-informed friend catching you up — flowing, no headers.',
    format: 'prose',
    length: 'short',
    bestseller: true,
  },
  {
    value: 'comprehensive',
    label: 'Comprehensive',
    desc: 'Full prose with sourcing and context per category.',
    format: 'prose',
    length: 'medium',
    bestseller: false,
  },
  {
    value: 'quick',
    label: 'Quick',
    desc: 'Tight bullets. Key facts only.',
    format: 'bullet',
    length: 'short',
    bestseller: false,
  },
  {
    value: 'analytical',
    label: 'Analytical',
    desc: 'Deep-dive with cross-source connections and Polari signals.',
    format: 'prose',
    length: 'long',
    bestseller: false,
  },
] as const

type StyleValue = typeof STYLES[number]['value']

export function EchoStyleStep({ eyebrow, accentClass, btnClass, initialData, onNext, onBack }: EchoStyleStepProps) {
  const [briefingStyle, setBriefingStyle] = useState<StyleValue>(
    (initialData?.briefing_style as StyleValue) ?? 'conversational'
  )

  const handleNext = () => {
    const chosen = STYLES.find(s => s.value === briefingStyle) ?? STYLES[0]
    onNext({
      briefing_style: briefingStyle,
      template_style: initialData?.template_style ?? 'modern',
      format_style: chosen.format,
      prose_paragraph_length: chosen.length,
      enable_story_integration: true,
    })
  }

  return (
    <>
      <div className="ob-body">
        {/* Eyebrow with inline Echo badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span className={`ob-eyebrow ${accentClass}`} style={{ marginBottom: 0 }}>{eyebrow}</span>
          <span style={{
            fontSize: 9, fontWeight: 600, padding: '2px 7px', borderRadius: 4,
            background: 'rgba(80,180,255,0.1)', color: 'rgba(80,180,255,.85)',
            border: '0.5px solid rgba(80,180,255,.2)',
            fontFamily: 'var(--font-mono, monospace)', letterSpacing: '0.04em',
          }}>+ Echo signals</span>
        </div>

        <h2 className="ob-heading">How should it read?</h2>
        <p className="ob-sub">Reddit + Bluesky discourse woven in on every style.</p>

        {/* Style cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {STYLES.map(s => {
            const on = briefingStyle === s.value
            return (
              <button
                key={s.value}
                type="button"
                onClick={() => setBriefingStyle(s.value)}
                style={{
                  textAlign: 'left', cursor: 'pointer',
                  background: on ? 'rgba(0,255,192,0.03)' : 'var(--color-background-secondary)',
                  border: `0.5px solid ${on ? 'rgba(0,255,192,0.4)' : s.bestseller ? 'rgba(0,255,192,0.18)' : 'var(--color-border-tertiary)'}`,
                  borderRadius: 9, padding: '11px 13px',
                  position: 'relative', overflow: 'hidden',
                  transition: 'border-color 0.12s',
                  minHeight: 60,
                }}
              >
                {(s.bestseller || on) && (
                  <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: 1,
                    background: 'linear-gradient(90deg,rgba(0,255,192,.5),rgba(102,0,255,.3),transparent 65%)',
                  }} />
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
                      <span style={{
                        fontSize: 13, fontWeight: 500,
                        color: on ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                      }}>{s.label}</span>
                      {s.bestseller && (
                        <span style={{
                          fontSize: 8, padding: '1px 6px', borderRadius: 3,
                          background: 'linear-gradient(90deg,rgba(0,255,192,.15),rgba(102,0,255,.1))',
                          border: '0.5px solid rgba(0,255,192,.3)',
                          color: '#00c896',
                          fontFamily: 'var(--font-mono, monospace)',
                        }}>best seller</span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', lineHeight: 1.45 }}>
                      {s.desc}
                    </div>
                  </div>
                  {on && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#00FFC0" strokeWidth="2.5" style={{ flexShrink: 0 }} aria-hidden="true">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
              </button>
            )
          })}
        </div>

        {/* Echo signal pills — compact, below cards */}
        <div style={{
          marginTop: 14,
          padding: '8px 12px',
          borderRadius: 8,
          border: '0.5px solid rgba(80,180,255,0.18)',
          background: 'rgba(80,180,255,0.04)',
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        }}>
          <span style={{
            fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
            background: 'rgba(80,180,255,0.1)', color: 'rgba(80,180,255,.85)',
            border: '0.5px solid rgba(80,180,255,.2)',
            fontFamily: 'var(--font-mono, monospace)', flexShrink: 0,
          }}>Echo</span>
          {['controversy', 'counter-narrative', 'velocity', 'sentiment'].map(s => (
            <span key={s} style={{ fontSize: 10, color: 'rgba(80,180,255,0.65)' }}>{s}</span>
          ))}
        </div>
      </div>

      <div className="ob-footer">
        <button type="button" className="ob-btn-back" onClick={onBack}>
          <ArrowLeft size={13} /> Back
        </button>
        <button type="button" className={`ob-btn-next ${btnClass}`} onClick={handleNext}>
          Continue <ArrowRight size={13} />
        </button>
      </div>
    </>
  )
}